import * as React from "react";
import { CheckCircle2, HelpCircle, XCircle } from "lucide-react";

import { Button } from "@/lib/shadcn/ui/button";
import { Skeleton } from "@/lib/shadcn/ui/skeleton";
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "@/lib/shadcn/ui/table";
import { SyncControlBar } from "@/lib/shadcn/components/sync-control-bar";
import { HostHubSyncIssue } from "@/models/responses";
import { useGetHostHubSyncIssuesQuery } from "@/store/hosthub-api";

const WEDGE_NOTICE =
    "A blocking event holds its rental's cursor: nothing after it is ingested until the cause is fixed. " +
    "Fix the cause (the reason below), then run the sync — there is nothing to dismiss here.";

function toDate(value: Date | string): Date | null {
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * How long the rental has been wedged, as words ("3 hours ago") rather than a
 * terse "3h" — the operator reads this in an incident, not at a glance.
 * hosthub-event-durability/FR-014.
 */
function formatAge(firstObservedAt: Date | string, now: number = Date.now()): string {
    const observed = toDate(firstObservedAt);
    if (!observed) {
        return "unknown";
    }
    const relative = new Intl.RelativeTimeFormat("en", { numeric: "always" });
    // A wedge is always already stuck, so the age is clamped to the past ONCE,
    // before branching: a browser clock running behind the server would
    // otherwise render "in 2 hours" on the hour/day branches.
    const minutes = Math.min(Math.round((observed.getTime() - now) / 60_000), -1);
    if (Math.abs(minutes) < 60) {
        return relative.format(minutes, "minute");
    }
    const hours = Math.round(minutes / 60);
    if (Math.abs(hours) < 24) {
        return relative.format(hours, "hour");
    }
    return relative.format(Math.round(hours / 24), "day");
}

/**
 * The skeleton bars are decorative and stay hidden from assistive tech; the
 * polite `role="status"` line is the only signal a screen-reader user gets that
 * a fetch is in flight, instead of a page that merely seems stalled (WCAG 4.1.3).
 */
const SyncIssuesSkeleton: React.FC = () => (
    <div className="space-y-3">
        <span role="status" className="sr-only">
            Loading sync issues…
        </span>
        <div data-testid="sync-issues-skeleton" className="space-y-3" aria-hidden="true">
            {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
            ))}
        </div>
    </div>
);

/**
 * `lastRunAt` is absent until a sync has run in this process, so the stamp is
 * rendered only when we have one. hosthub-event-durability/AS-014, FR-016.
 */
const FreshnessLine: React.FC<{ lastRunAt?: Date | string }> = ({ lastRunAt }) => {
    const runAt = lastRunAt === undefined ? null : toDate(lastRunAt);
    return (
        <p data-testid="sync-issues-freshness" className="text-sm text-muted-foreground">
            {runAt ? (
                <>
                    Reflects the sync run at{" "}
                    <time dateTime={runAt.toISOString()}>{runAt.toLocaleString("en-GB")}</time>.
                </>
            ) : (
                "Reflects the last sync run; its time is unknown."
            )}
        </p>
    );
};

/**
 * How long it has been stuck, in words — with the exact instant on the element
 * itself, because during an incident "3 hours ago" is not a timestamp anyone can
 * correlate with a log line (hosthub-event-durability/FR-014).
 */
const StuckSince: React.FC<{ firstObservedAt: Date | string }> = ({ firstObservedAt }) => {
    const observed = toDate(firstObservedAt);
    const age = formatAge(firstObservedAt);
    if (!observed) {
        return <>{age}</>;
    }
    return (
        <time dateTime={observed.toISOString()} title={observed.toLocaleString("en-GB")}>
            {age}
        </time>
    );
};

/**
 * One wedged rental. Read-only by design (FR-018): resolution is "fix the cause,
 * then run the sync", so the row deliberately offers no retry / dismiss / park
 * control that would let an operator hide a wedge instead of clearing it.
 */
const SyncIssueRow: React.FC<{ issue: HostHubSyncIssue }> = ({ issue }) => (
    <TableRow data-testid={`sync-issue-row-${issue.rentalId}`}>
        <TableCell data-testid={`sync-issue-apartment-${issue.rentalId}`} className="font-medium">
            {issue.apartmentName}
        </TableCell>
        <TableCell data-testid={`sync-issue-booking-${issue.rentalId}`}>
            {issue.bookingRef === "" ? "—" : issue.bookingRef}
        </TableCell>
        <TableCell data-testid={`sync-issue-event-type-${issue.rentalId}`}>{issue.eventType}</TableCell>
        <TableCell data-testid={`sync-issue-reason-${issue.rentalId}`} className="text-sm text-slate-700">
            {issue.reason}
        </TableCell>
        <TableCell data-testid={`sync-issue-age-${issue.rentalId}`} className="whitespace-nowrap">
            <StuckSince firstObservedAt={issue.firstObservedAt} />
        </TableCell>
        <TableCell data-testid={`sync-issue-waiting-${issue.rentalId}`} className="text-right">
            {issue.waitingCount}
        </TableCell>
    </TableRow>
);

/**
 * SyncIssuesPage (hosthub-event-durability/AS-012, AS-014, AS-016) — the rentals
 * whose HostHub cursor is currently held by a blocking event, and how fresh that
 * picture is.
 *
 * The two empty states are deliberately distinct and must NOT be collapsed: an
 * empty list before any run in this process means "we do not know yet"
 * (FR-017), while an empty list after a run means "nothing is stuck" (EC-007).
 * The incident this feature answers was an alert that read healthy while a
 * booking was silently lost.
 */
const SyncIssuesPage: React.FC = () => {
    const { data, isLoading, isError, refetch } = useGetHostHubSyncIssuesQuery();

    const issues = data?.issues ?? [];
    const hasRun = data?.hasRun === true;
    const loaded = !isLoading && !isError && data !== undefined;

    return (
        <div data-testid="sync-issues-page" className="flex flex-col gap-4">
            <h1 className="text-3xl font-bold">Sync issues</h1>
            <p data-testid="sync-issues-notice" className="text-sm text-slate-700">
                {WEDGE_NOTICE}
            </p>

            {/*
             * Run-now only: there are no cancelled rows to filter on an incident
             * page, and a control that does nothing erodes trust in the surface
             * the operator is meant to believe (FR-018).
             */}
            <SyncControlBar
                showCancelledFilter={false}
                hideCancelled={false}
                onHideCancelledChange={() => undefined}
            />

            {isLoading && <SyncIssuesSkeleton />}

            {!isLoading && isError && (
                <div
                    data-testid="sync-issues-error-panel"
                    role="alert"
                    className="flex flex-col items-center gap-4 py-8 text-center"
                >
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600">
                        <XCircle className="h-6 w-6" aria-hidden="true" />
                    </div>
                    <p className="max-w-md text-sm text-slate-700">The sync-issues list could not be loaded.</p>
                    <Button data-testid="sync-issues-retry" onClick={() => void refetch()}>
                        Retry
                    </Button>
                </div>
            )}

            {loaded && hasRun && <FreshnessLine lastRunAt={data.lastRunAt} />}

            {loaded && !hasRun && (
                <div
                    data-testid="sync-issues-unknown"
                    role="status"
                    className="flex flex-col items-center gap-3 rounded-md border border-amber-300 bg-amber-50 py-8 text-center text-amber-800"
                >
                    <HelpCircle className="h-6 w-6" aria-hidden="true" />
                    <p className="max-w-md text-sm">
                        No sync has run since this server started, so the sync state is not yet known. Run the sync to
                        find out.
                    </p>
                </div>
            )}

            {loaded && hasRun && issues.length === 0 && (
                <div
                    data-testid="sync-issues-all-clear"
                    role="status"
                    className="flex flex-col items-center gap-3 py-8 text-center"
                >
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-600">
                        <CheckCircle2 className="h-6 w-6" aria-hidden="true" />
                    </div>
                    <p className="text-sm text-slate-700">Every rental is up to date — nothing is stuck.</p>
                </div>
            )}

            {loaded && issues.length > 0 && (
                <Table data-testid="sync-issues-table">
                    {/*
                     * Reached directly by table navigation the grid is otherwise
                     * anonymous, so it says what it lists; the caption is for
                     * assistive tech only — the heading above already says it
                     * visually (WCAG 1.3.1).
                     */}
                    <TableCaption className="sr-only">
                        Rentals whose HostHub cursor is currently held by a blocking event
                    </TableCaption>
                    <TableHeader>
                        <TableRow>
                            <TableHead scope="col">Apartment</TableHead>
                            <TableHead scope="col">Booking</TableHead>
                            <TableHead scope="col">Event type</TableHead>
                            <TableHead scope="col">Why it is stuck</TableHead>
                            <TableHead scope="col">Stuck since</TableHead>
                            <TableHead scope="col" className="text-right">
                                Events waiting
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {issues.map((issue) => (
                            <SyncIssueRow key={issue.rentalId} issue={issue} />
                        ))}
                    </TableBody>
                </Table>
            )}
        </div>
    );
};

export default SyncIssuesPage;
